/*
 * Ajuda de digitação nos textos livres: maiúscula no começo da frase e correção
 * de acentos óbvios. Roda quando uma palavra termina (espaço, pontuação ou
 * quebra de linha) — assim links, e-mails e siglas nunca são tocados.
 * Aqui só a regra pura; quem aplica no campo é `use-typing-assist.ts`.
 */

/**
 * Só palavras que, sem acento, não existem em pt-BR (nenhuma é forma verbal
 * ou outra palavra válida). Na dúvida, fica de fora: "esta", "e", "numero",
 * "pratica", "analise", "media" etc. NÃO entram.
 */
export const CORRECTIONS: Readonly<Record<string, string>> = {
  nao: 'não',
  voce: 'você',
  voces: 'vocês',
  tambem: 'também',
  ja: 'já',
  entao: 'então',
  ate: 'até',
  sera: 'será',
  porem: 'porém',
  alem: 'além',
  apos: 'após',
  atraves: 'através',
  alguem: 'alguém',
  ninguem: 'ninguém',
  sao: 'são',
  estao: 'estão',
  vao: 'vão',
  mes: 'mês',
  tres: 'três',
  amanha: 'amanhã',
  sabado: 'sábado',
  terca: 'terça',
  proximo: 'próximo',
  proxima: 'próxima',
  periodo: 'período',
  possivel: 'possível',
  disponivel: 'disponível',
  responsavel: 'responsável',
  necessario: 'necessário',
  necessaria: 'necessária',
  rapido: 'rápido',
  facil: 'fácil',
  dificil: 'difícil',
  nivel: 'nível',
  relatorio: 'relatório',
  horario: 'horário',
  calendario: 'calendário',
  usuario: 'usuário',
  funcionario: 'funcionário',
  logistica: 'logística',
  orcamento: 'orçamento',
  preco: 'preço',
  servico: 'serviço',
  endereco: 'endereço',
  informacao: 'informação',
  acao: 'ação',
  acoes: 'ações',
  reuniao: 'reunião',
  producao: 'produção',
  manutencao: 'manutenção',
  solucao: 'solução',
  situacao: 'situação',
  atencao: 'atenção',
  funcao: 'função',
  questao: 'questão',
  previsao: 'previsão',
  decisao: 'decisão',
  revisao: 'revisão',
  aprovacao: 'aprovação',
  operacao: 'operação',
  comunicacao: 'comunicação',
  cotacao: 'cotação',
  cotacoes: 'cotações',
  requisicao: 'requisição',
  negociacao: 'negociação',
  devolucao: 'devolução',
  instalacao: 'instalação',
  inspecao: 'inspeção',
  programacao: 'programação',
  conclusao: 'conclusão',
  atualizacao: 'atualização',
}

/** Abreviações comuns: o ponto delas não encerra frase. */
const ABBREVIATIONS = new Set(['ex', 'sr', 'sra', 'dr', 'dra', 'prof', 'aprox', 'av', 'tel', 'obs', 'pág', 'cap'])

const BOUNDARY = /[\s.,;:!?)\]]/
const TRAILING = /[.,;:!?)\]]/
const WORD_AT_END = /(\p{L}+)$/u
/** Antes da palavra só pode haver começo, espaço ou abertura — nada de "/", "@", ".", "-", "_" ou dígito. */
const WORD_START = /[\s(["'“‘«]/
const SENTENCE_START = /(?:^|[.!?…]\s|\n)[\s(["'“‘«]*$/u
const ABBREVIATION_BEFORE = /(\p{L}+)\.\s[\s(["'“‘«]*$/u
const LOWER_WORD = /^\p{Ll}+$/u
const CAPITALIZED_WORD = /^\p{Lu}\p{Ll}*$/u

const capitalize = (w: string) => w.charAt(0).toLocaleUpperCase('pt-BR') + w.slice(1)

/** Correção da lista, mantendo "Nao" → "Não". Tudo maiúsculo (sigla, grito) não muda. */
export function correctWord(word: string): string {
  const lower = word.toLocaleLowerCase('pt-BR')
  const fixed = CORRECTIONS[lower]
  if (!fixed) return word
  if (LOWER_WORD.test(word)) return fixed
  if (CAPITALIZED_WORD.test(word)) return capitalize(fixed)
  return word
}

/** O texto antes da palavra termina num começo de frase? */
export function isSentenceStart(before: string): boolean {
  if (!SENTENCE_START.test(before)) return false
  const abbr = ABBREVIATION_BEFORE.exec(before)
  return !(abbr && ABBREVIATIONS.has(abbr[1]!.toLocaleLowerCase('pt-BR')))
}

export type Fix = { start: number; end: number; replacement: string }

/**
 * Chamado logo depois de digitar um caractere (o cursor está depois dele).
 * Se esse caractere fecha uma palavra, devolve a troca a fazer na palavra; senão null.
 */
export function assistAt(text: string, caret: number): Fix | null {
  const boundary = text.charAt(caret - 1)
  if (caret < 2 || !BOUNDARY.test(boundary)) return null
  // No espaço, olha a palavra antes da pontuação ("ok. " → "Ok. ").
  let end = caret - 1
  if (/\s/.test(boundary)) while (end > 0 && TRAILING.test(text.charAt(end - 1))) end--
  const before = text.slice(0, end)
  const match = WORD_AT_END.exec(before)
  if (!match) return null
  const word = match[1]!
  const start = before.length - word.length
  if (start > 0 && !WORD_START.test(before.charAt(start - 1))) return null
  const prefix = before.slice(0, start)
  // Dentro de `código` (crase aberta na linha) não mexe.
  if ((prefix.slice(prefix.lastIndexOf('\n') + 1).match(/`/g)?.length ?? 0) % 2 === 1) return null

  let next = correctWord(word)
  // "www." e "https:" ainda podem virar link: a maiúscula espera o espaço.
  const mayCapitalize = boundary !== '.' && boundary !== ':'
  if (mayCapitalize && LOWER_WORD.test(word) && isSentenceStart(prefix)) next = capitalize(next)
  return next === word ? null : { start, end, replacement: next }
}
